import {
	Modality,
	type ProjectParameter,
	type Topic,
	type InstanceDescription,
	type Lever,
	type AbstractResource,
	type AdapterService,
	type FunctionContract,
	type ResourceSchema
} from '@semanticedge/persistence-service-noheaders-client/src'
import type { NluConfig } from '../models/nlu-config.ts'
import type { DetailedSelectItem } from '../models/detailed-select-item.ts'
import type { IconName } from '../svg-icons/iconsMap.ts'
import type { FlowViewModel } from '../models/project/flow.ts'
import {
	DetailedSelectItemableElementType,
	type IdAndNamedElement,
	type ReleaseVersionSelectable,
	type EmbeddingModelSelectable
} from '../models/project/flow-view.ts'
import type { Node as FlowNode } from '@xyflow/svelte'
import type {
	IntentSpectrum,
	KnowledgeCollectionDTO,
	Persona,
	CompanyResponse,
	Tag
} from '@semanticedge/knowledge-service-noheaders-client/src'
import { EmbeddingStatus } from '../constants/embedding-status/index.js'
import { injectTranslation } from './translation.ts'

type Mapper<T> = (element: T) => DetailedSelectItem

const formatDateTime = (date: Date | string | undefined): string => {
	if (!date) return ''
	const d = new Date(date)
	const pad = (n: number) => String(n).padStart(2, '0')
	return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export type TopicSelectOption = {
	id: string
	name: string
	customTypeEnum?: Topic['customTypeEnum']
	resourceSchemaId?: Topic['resourceSchemaId']
	description?: Topic['description']
}

export type ElementTypeMap = {
	[DetailedSelectItemableElementType.TOPIC]: TopicSelectOption
	[DetailedSelectItemableElementType.NLU_CONFIG]: NluConfig
	[DetailedSelectItemableElementType.INSTANCE]: InstanceDescription
	[DetailedSelectItemableElementType.FLOW]: FlowViewModel
	[DetailedSelectItemableElementType.NODE]: FlowNode
	[DetailedSelectItemableElementType.INTENT_SPECTRUM]: IntentSpectrum
	[DetailedSelectItemableElementType.PERSONA]: Persona
	[DetailedSelectItemableElementType.COMPANY]: CompanyResponse
	[DetailedSelectItemableElementType.LEVER]: Lever
	[DetailedSelectItemableElementType.GSP]: ProjectParameter
	[DetailedSelectItemableElementType.ADAPTER_SERVICE]: AdapterService
	[DetailedSelectItemableElementType.FUNCTION_CONTRACT]: FunctionContract
	[DetailedSelectItemableElementType.CUSTOM_TYPE]: ResourceSchema
	[DetailedSelectItemableElementType.REFERENCE_TABLE]: AbstractResource
	[DetailedSelectItemableElementType.KNOWLEDGE_COLLECTION]: KnowledgeCollectionDTO
	[DetailedSelectItemableElementType.RELEASE_VERSION]: ReleaseVersionSelectable
	[DetailedSelectItemableElementType.EMBEDDING_MODEL]: EmbeddingModelSelectable
	[DetailedSelectItemableElementType.TAG]: Tag
}

const ELEMENT_MAPPERS: {
	[K in DetailedSelectItemableElementType]: Mapper<ElementTypeMap[K]>
} = {
	[DetailedSelectItemableElementType.TOPIC]: ({ id, name }: IdAndNamedElement) => ({
		id: id!,
		title: name!,
		description: ''
	}),

	[DetailedSelectItemableElementType.NLU_CONFIG]: ({
		id,
		modelName,
		modelVersion,
		description
	}: NluConfig) => ({
		id: id!,
		title: `${modelName!} ${modelVersion ? `v${modelVersion}` : ''}`.trim(),
		description: description ?? ''
	}),

	[DetailedSelectItemableElementType.INSTANCE]: ({
		id,
		name,
		description,
		applicationName,
		applicationVersion
	}: InstanceDescription) => {
		const appInfo = `${applicationName!} v${applicationVersion!}`

		return {
			id: id!,
			title: name!,
			description: description ? `${description} (${appInfo})` : appInfo
		}
	},

	[DetailedSelectItemableElementType.FLOW]: ({ id, name }: FlowViewModel) => ({
		id: id!,
		title: name!,
		description: ''
	}),

	[DetailedSelectItemableElementType.NODE]: ({ id, type, data }: FlowNode) => ({
		id: id!,
		title: ((data as { label?: string }).label ?? type ?? id!) as string,
		description: type ?? ''
	}),

	[DetailedSelectItemableElementType.INTENT_SPECTRUM]: ({
		id,
		name,
		description
	}: IntentSpectrum) => ({
		id: id!,
		title: name!,
		description: description ?? ''
	}),

	[DetailedSelectItemableElementType.PERSONA]: ({ id, name, description, avatar }: Persona) => ({
		id: id!,
		title: name!,
		description: description ?? '',
		imgPayload: avatar
	}),
	[DetailedSelectItemableElementType.COMPANY]: ({ id, name, description }: CompanyResponse) => ({
		id: id!,
		title: name!,
		description: description ?? ''
	}),

	[DetailedSelectItemableElementType.LEVER]: ({ id, name, description, defaultValue }: Lever) => ({
		id: id!,
		title: name!,
		description:
			`${description ?? ''} ${defaultValue ? `(Default: ${JSON.stringify(defaultValue)})` : ''}`.trim()
	}),
	[DetailedSelectItemableElementType.REFERENCE_TABLE]: ({
		id,
		name,
		description
	}: AbstractResource) => ({
		id: id!,
		title: name!,
		description: description ?? ''
	}),

	[DetailedSelectItemableElementType.GSP]: ({
		id,
		name,
		description,
		projectParameterType
	}: ProjectParameter) => ({
		id: id!,
		title: `${name!} ${projectParameterType ? `(${projectParameterType})` : ''}`.trim(),
		description: description ?? ''
	}),

	[DetailedSelectItemableElementType.ADAPTER_SERVICE]: ({
		id,
		name,
		description
	}: AdapterService) => ({
		id: id!,
		title: name!,
		description: description ?? ''
	}),

	[DetailedSelectItemableElementType.FUNCTION_CONTRACT]: ({
		id,
		name,
		functionType
	}: FunctionContract) => ({
		id: id!,
		title: name!,
		description: functionType
			? injectTranslation('FunctionContract.returns', { type: functionType })
			: injectTranslation('FunctionContract.nothing')
	}),

	[DetailedSelectItemableElementType.CUSTOM_TYPE]: ({ id, name }: ResourceSchema) => ({
		id: id!,
		title: name!,
		description: ''
	}),

	[DetailedSelectItemableElementType.KNOWLEDGE_COLLECTION]: ({
		id,
		name
	}: KnowledgeCollectionDTO) => ({
		id: id!,
		title: name!,
		description: ''
	}),

	[DetailedSelectItemableElementType.RELEASE_VERSION]: ({
		id,
		releaseVersion,
		createdBy,
		createdAt,
		releaseNotes,
		isLatestDraft
	}: ReleaseVersionSelectable) => {
		const notes = releaseNotes?.trim()
		const dateStr = formatDateTime(createdAt)
		const meta = createdBy ? `${createdBy} - ${dateStr}` : dateStr
		return {
			id: id ?? '',
			title: releaseVersion ?? '',
			description: notes || meta,
			isLatestDraft: isLatestDraft ?? false
		}
	},

	[DetailedSelectItemableElementType.TAG]: ({ id, name, description }: Tag) => ({
		id: id ?? '',
		title: name ?? '',
		description: description ?? ''
	}),

	[DetailedSelectItemableElementType.EMBEDDING_MODEL]: ({
		embeddingModelId,
		name,
		status
	}: EmbeddingModelSelectable) => {
		const iconConfig: Record<string, { name: IconName; class: string }> = {
			[EmbeddingStatus.SYNCED]: {
				name: 'checkCircle',
				class: 'bg-[#0ACC920D] text-[#067A57CC]'
			},
			[EmbeddingStatus.SYNCING]: {
				name: 'refreshThin',
				class: 'bg-[#007ACC0D] text-[#007ACCCC]'
			},
			[EmbeddingStatus.OUT_OF_SYNC]: {
				name: 'error',
				class: 'bg-[#FFC02E0D] text-[#8F6C1ACC]'
			}
		}
		const endIcon = status ? iconConfig[status] : iconConfig[EmbeddingStatus.SYNCED]
		return {
			id: embeddingModelId ?? '',
			title: name ?? '',
			startIcon: { name: 'openai', size: 'lg' },
			endIcon: { name: endIcon.name, size: 'sm', class: `${endIcon.class} p-0.5 rounded-full` }
		}
	}
}

export const elementToDetailedSelectItem = <T extends DetailedSelectItemableElementType>(
	element: ElementTypeMap[T],
	type: T
): DetailedSelectItem => {
	const mapper = ELEMENT_MAPPERS[type]
	if (!mapper) throw new Error(`Unsupported element type: ${type}`)
	return mapper(element)
}

export const initStateRuneForEachModality: <S, T>(
	source: Record<string, S>, // actually Record<Modality, S>, but the generated client code uses string
	mapper: (value: S, modality: Modality) => T
) => Record<Modality, T> = <S, T>(
	source: Record<string, S>,
	mapper: (value: S, modality: Modality) => T
): Record<Modality, T> => {
	const result: Record<Modality, T> = {} as Record<Modality, T>

	for (const modalityKey in source) {
		const modality: Modality = modalityKey as Modality
		const value: S = source[modalityKey]
		const mappedValue: T = mapper(value, modality)
		result[modality] = mappedValue
	}

	return result
}
